import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { 
  BarChart3, 
  Eye, 
  MousePointer, 
  Play, 
  TrendingUp, 
  Users, 
  Calendar,
  RefreshCw,
  Package,
  Clock,
  Globe,
  Smartphone,
  Monitor
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Card de Estatística
const StatCard = ({ title, value, icon: Icon, color = 'orange', subtitle, trend }) => (
  <div className="bg-card border border-border rounded-xl p-5 hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-muted-foreground mb-1">{title}</p>
        <p className="text-3xl font-bold">{value}</p>
        {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
        {trend && (
          <p className={`text-xs mt-1 flex items-center gap-1 ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
            <TrendingUp className={`w-3 h-3 ${trend < 0 ? 'rotate-180' : ''}`} />
            {Math.abs(trend)}% vs período anterior
          </p>
        )}
      </div>
      <div className={`p-3 rounded-xl bg-${color}-100 text-${color}-600`}>
        <Icon className="w-6 h-6" />
      </div>
    </div>
  </div>
);

// Barra de Progresso
const ProgressBar = ({ value, max, color = 'orange' }) => {
  const percentage = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="w-full bg-muted rounded-full h-2">
      <div 
        className={`h-2 rounded-full bg-${color}-500 transition-all duration-500`}
        style={{ width: `${Math.min(percentage, 100)}%` }}
      />
    </div>
  );
};

// Componente de Ranking
const RankingItem = ({ rank, name, count, maxCount, subtitle }) => (
  <div className="flex items-center gap-4 py-3 border-b border-border last:border-0">
    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
      rank === 1 ? 'bg-yellow-100 text-yellow-700' :
      rank === 2 ? 'bg-gray-100 text-gray-700' :
      rank === 3 ? 'bg-orange-100 text-orange-700' :
      'bg-muted text-muted-foreground'
    }`}>
      {rank}
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-medium truncate">{name}</p>
      {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      <ProgressBar value={count} max={maxCount} />
    </div>
    <div className="text-right">
      <p className="font-bold text-lg">{count}</p>
      <p className="text-xs text-muted-foreground">visualizações</p>
    </div>
  </div>
);

export const Analytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('7d'); // 7d, 30d, 90d, all
  const [refreshing, setRefreshing] = useState(false);

  const fetchAnalytics = async () => {
    try {
      setRefreshing(true);
      const response = await axios.get(`${API}/analytics/dashboard?period=${period}`);
      setData(response.data);
    } catch (error) {
      console.error('Erro ao carregar analytics:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  // Auto-refresh a cada 30 segundos
  useEffect(() => {
    const interval = setInterval(fetchAnalytics, 30000);
    return () => clearInterval(interval);
  }, [period]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const {
    total_page_views = 0,
    total_product_views = 0,
    total_product_clicks = 0,
    total_video_views = 0,
    unique_visitors = 0,
    top_products = [],
    top_clicked = [],
    video_stats = [],
    views_by_day = [],
    device_stats = { desktop: 0, mobile: 0, tablet: 0 }
  } = data || {};

  const maxViews = Math.max(...top_products.map(p => p.views), 1);
  const maxClicks = Math.max(...top_clicked.map(p => p.clicks), 1);
  const maxVideoViews = Math.max(...video_stats.map(v => v.views), 1);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-3xl font-heading font-bold">Analytics do Site</h2>
          <p className="text-muted-foreground mt-1">Acompanhe o desempenho do seu catálogo</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Seletor de Período */}
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="input-field text-sm"
            data-testid="period-selector"
          >
            <option value="7d">Últimos 7 dias</option>
            <option value="30d">Últimos 30 dias</option>
            <option value="90d">Últimos 90 dias</option>
            <option value="all">Todo o período</option>
          </select>
          <button
            onClick={fetchAnalytics}
            disabled={refreshing}
            className="btn-secondary flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </div>
      </div>

      {/* Cards de Estatísticas */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Visitantes Únicos"
          value={unique_visitors.toLocaleString()}
          icon={Users}
          color="blue"
          subtitle="Pessoas diferentes"
        />
        <StatCard
          title="Visualizações de Página"
          value={total_page_views.toLocaleString()}
          icon={Eye}
          color="green"
          subtitle="Total de acessos"
        />
        <StatCard
          title="Cliques em Produtos"
          value={total_product_clicks.toLocaleString()}
          icon={MousePointer}
          color="orange"
          subtitle="Interesse demonstrado"
        />
        <StatCard
          title="Vídeos Assistidos"
          value={total_video_views.toLocaleString()}
          icon={Play}
          color="purple"
          subtitle="Reproduções de vídeo"
        />
      </div>

      {/* Dispositivos */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Globe className="w-5 h-5 text-primary" />
          Dispositivos dos Visitantes
        </h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-4 bg-muted/50 rounded-xl">
            <Monitor className="w-8 h-8 mx-auto mb-2 text-blue-600" />
            <p className="text-2xl font-bold">{device_stats.desktop || 0}</p>
            <p className="text-sm text-muted-foreground">Desktop</p>
          </div>
          <div className="text-center p-4 bg-muted/50 rounded-xl">
            <Smartphone className="w-8 h-8 mx-auto mb-2 text-green-600" />
            <p className="text-2xl font-bold">{device_stats.mobile || 0}</p>
            <p className="text-sm text-muted-foreground">Mobile</p>
          </div>
          <div className="text-center p-4 bg-muted/50 rounded-xl">
            <Smartphone className="w-8 h-8 mx-auto mb-2 text-orange-600 rotate-90" />
            <p className="text-2xl font-bold">{device_stats.tablet || 0}</p>
            <p className="text-sm text-muted-foreground">Tablet</p>
          </div>
        </div>
      </div>

      {/* Rankings */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Produtos Mais Visualizados */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Eye className="w-5 h-5 text-green-600" />
            Produtos Mais Visualizados
          </h3>
          {top_products.length > 0 ? (
            <div className="space-y-1">
              {top_products.slice(0, 10).map((product, index) => (
                <RankingItem
                  key={product.product_id}
                  rank={index + 1}
                  name={product.product_name}
                  count={product.views}
                  maxCount={maxViews}
                  subtitle={`Código: ${product.product_code || 'N/A'}`}
                />
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              Nenhuma visualização registrada ainda
            </p>
          )}
        </div>

        {/* Produtos Mais Clicados */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <MousePointer className="w-5 h-5 text-orange-600" />
            Produtos Mais Clicados
          </h3>
          {top_clicked.length > 0 ? (
            <div className="space-y-1">
              {top_clicked.slice(0, 10).map((product, index) => (
                <RankingItem
                  key={product.product_id}
                  rank={index + 1}
                  name={product.product_name}
                  count={product.clicks}
                  maxCount={maxClicks}
                />
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              Nenhum clique registrado ainda
            </p>
          )}
        </div>
      </div>

      {/* Estatísticas de Vídeos */}
      {video_stats.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Play className="w-5 h-5 text-purple-600" />
            Vídeos Mais Assistidos
          </h3>
          <div className="space-y-1">
            {video_stats.slice(0, 10).map((video, index) => (
              <RankingItem
                key={`${video.product_id}-${video.video_index}`}
                rank={index + 1}
                name={video.product_name}
                count={video.views}
                maxCount={maxVideoViews}
                subtitle={`Vídeo ${video.video_index + 1}`}
              />
            ))}
          </div>
        </div>
      )}

      {/* Gráfico de Visitas por Dia */}
      {views_by_day.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            Visitas por Dia
          </h3>
          <div className="h-48 flex items-end gap-1">
            {views_by_day.map((day) => {
              const maxDayViews = Math.max(...views_by_day.map(d => d.views), 1);
              const height = (day.views / maxDayViews) * 100;
              return (
                <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-[10px] text-muted-foreground">{day.views}</span>
                  <div 
                    className="w-full bg-primary/80 hover:bg-primary rounded-t transition-all"
                    style={{ height: `${Math.max(height, 2)}%` }}
                    title={`${day.date}: ${day.views} visitas`}
                  />
                  <span className="text-[10px] text-muted-foreground rotate-45 origin-left whitespace-nowrap">
                    {new Date(day.date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Sistema Operacional, Navegador, Horários */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* SO */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="font-semibold mb-3 flex items-center gap-2 text-sm">
            <Smartphone className="w-4 h-4 text-green-600" />
            Sistema Operacional
          </h3>
          {(data.os_stats || []).length > 0 ? (
            <div className="space-y-2">
              {(data.os_stats || []).map((os, i) => {
                const maxOs = Math.max(...(data.os_stats || []).map(o => o.count), 1);
                return (
                  <div key={i}>
                    <div className="flex justify-between text-xs mb-0.5">
                      <span className="font-medium">{os.name}</span>
                      <span className="text-muted-foreground">{os.count}</span>
                    </div>
                    <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-green-500 rounded-full" style={{ width: `${(os.count / maxOs) * 100}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <p className="text-xs text-muted-foreground">Sem dados</p>}
        </div>

        {/* Navegador */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="font-semibold mb-3 flex items-center gap-2 text-sm">
            <Globe className="w-4 h-4 text-blue-600" />
            Navegador
          </h3>
          {(data.browser_stats || []).length > 0 ? (
            <div className="space-y-2">
              {(data.browser_stats || []).map((br, i) => {
                const maxBr = Math.max(...(data.browser_stats || []).map(b => b.count), 1);
                return (
                  <div key={i}>
                    <div className="flex justify-between text-xs mb-0.5">
                      <span className="font-medium">{br.name}</span>
                      <span className="text-muted-foreground">{br.count}</span>
                    </div>
                    <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-blue-500 rounded-full" style={{ width: `${(br.count / maxBr) * 100}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <p className="text-xs text-muted-foreground">Sem dados</p>}
        </div>

        {/* Horários */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="font-semibold mb-3 flex items-center gap-2 text-sm">
            <Clock className="w-4 h-4 text-purple-600" />
            Horários de Pico
          </h3>
          {(data.hourly_stats || []).length > 0 ? (
            <div className="flex items-end gap-[2px] h-24">
              {Array.from({ length: 24 }, (_, h) => {
                const stat = (data.hourly_stats || []).find(s => s.hour === h);
                const count = stat?.count || 0;
                const maxH = Math.max(...(data.hourly_stats || []).map(s => s.count), 1);
                const height = count > 0 ? (count / maxH) * 100 : 2;
                return (
                  <div key={h} className="flex-1 flex flex-col items-center" title={`${h}h: ${count} visitas`}>
                    <div className={`w-full rounded-t transition-all ${count > 0 ? 'bg-purple-500/80 hover:bg-purple-500' : 'bg-muted'}`}
                      style={{ height: `${height}%` }} />
                  </div>
                );
              })}
            </div>
          ) : <p className="text-xs text-muted-foreground">Sem dados</p>}
          <div className="flex justify-between text-[9px] text-muted-foreground mt-1">
            <span>0h</span><span>6h</span><span>12h</span><span>18h</span><span>23h</span>
          </div>
        </div>
      </div>

      {/* Dicas e Insights */}
      <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-xl p-6">
        <h3 className="font-semibold text-orange-800 mb-3">Insights e Recomendações</h3>
        <ul className="space-y-2 text-sm text-orange-700">
          {total_video_views < total_product_clicks * 0.1 && (
            <li>• Seus vídeos têm baixa visualização. Considere adicionar mais vídeos aos produtos populares.</li>
          )}
          {device_stats.mobile > device_stats.desktop && (
            <li>• A maioria dos seus visitantes usa celular. Garanta que as imagens estejam otimizadas para mobile.</li>
          )}
          {top_products.length > 0 && (
            <li>• O produto "{top_products[0]?.product_name}" é o mais visualizado. Considere destacá-lo no catálogo.</li>
          )}
          {total_product_clicks < total_page_views * 0.3 && (
            <li>• Taxa de clique baixa. Melhore as fotos e descrições para atrair mais interesse.</li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default Analytics;
